import type {Dayjs} from 'dayjs';
import dayjs from 'dayjs';
import {Clock} from 'lucide-react';
import {useEffect, useState} from 'react';

import type {ComboboxProps} from '@/components/ui/combobox';
import {Combobox} from '@/components/ui/combobox';
import {PartOfTheDay} from '@/services/graphqlHooks';
import partOfTheDay from '@/translation/partOfTheDay';
import {TIME_FORMATS} from '@/utils/timeFormats';
import {cn} from '@/utils/ui';

import {
  getPartOfTheDayClasses,
  getPartOfTheDayIcon,
} from '../shared/tasks/utils/tasksHelpers';

export type TaskTimeOption = {
  value: string;
  label: string;
  keywords: string[];
};

export type TaskTimeInputProps = Omit<
  ComboboxProps<TaskTimeOption>,
  'items'
> & {
  isHourInterval: boolean;
  value: string;
  onChange: (value: string) => void;
};

export const timeFormat = /^([01]\d|2[0-3]):([0-5]\d)$/;

const getTodayWithTime = (hours: number, minutes: number) => {
  return dayjs()
    .set('hour', hours)
    .set('minute', minutes)
    .set('second', 0)
    .set('millisecond', 0);
};

const generateTaskTimeOptions = (
  startTime: Dayjs,
  endTime: Dayjs
): TaskTimeOption[] => {
  const taskTimeOptions = [];
  let currentTime = startTime;

  while (currentTime.isBefore(endTime)) {
    const formattedTime = currentTime.format(TIME_FORMATS.time);
    taskTimeOptions.push({
      value: formattedTime,
      label: formattedTime,
      keywords: [formattedTime],
    });
    currentTime = currentTime.add(30, 'minute');
  }

  return taskTimeOptions;
};

const getPartOfTheDateOptions = (): TaskTimeOption[] => [
  {
    value: PartOfTheDay.Morning,
    label: partOfTheDay[PartOfTheDay.Morning],
    keywords: [partOfTheDay[PartOfTheDay.Morning]],
  },
  {
    value: PartOfTheDay.Afternoon,
    label: partOfTheDay[PartOfTheDay.Afternoon],
    keywords: [partOfTheDay[PartOfTheDay.Afternoon]],
  },
  {
    value: PartOfTheDay.Evening,
    label: partOfTheDay[PartOfTheDay.Evening],
    keywords: [partOfTheDay[PartOfTheDay.Evening]],
  },
  {
    value: PartOfTheDay.Night,
    label: partOfTheDay[PartOfTheDay.Night],
    keywords: [partOfTheDay[PartOfTheDay.Night]],
  },
];

const hourIntervalTaskTimeOptions = [
  ...generateTaskTimeOptions(getTodayWithTime(0, 0), getTodayWithTime(24, 0)),
] as TaskTimeOption[];

const defaultTaskTimeOptions = [
  ...getPartOfTheDateOptions(),
  ...hourIntervalTaskTimeOptions,
] as TaskTimeOption[];

const TaskTimeInput = ({
  value,
  onChange,
  isHourInterval,
  ...props
}: TaskTimeInputProps) => {
  const [inputValue, setInputValue] = useState(props.inputValue || '');
  const [taskTimeOptions, setTaskTimeOptions] = useState(
    defaultTaskTimeOptions
  );

  useEffect(() => {
    setTaskTimeOptions(
      isHourInterval ? hourIntervalTaskTimeOptions : defaultTaskTimeOptions
    );
  }, [isHourInterval]);

  const handleInputValueChange = ({inputValue}: {inputValue: string}) => {
    setInputValue(inputValue || '');

    if (!inputValue || !timeFormat.test(inputValue)) return;

    const shouldAdd = !taskTimeOptions.some(
      option => option.value === inputValue
    );

    if (!shouldAdd) return;

    setTaskTimeOptions(prevOptions => {
      const newOption = {
        value: inputValue,
        label: inputValue,
        keywords: [inputValue],
      };

      return [...prevOptions, newOption];
    });
  };

  const selectedItem =
    taskTimeOptions.find(options => options.value === value) || null;

  const filteredItems = taskTimeOptions.filter(item =>
    item.keywords.some(keyword =>
      keyword.toLowerCase().includes(inputValue.toLowerCase())
    )
  );

  useEffect(() => {
    handleInputValueChange({
      inputValue: selectedItem?.label || value,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value, selectedItem?.label]);

  return (
    <Combobox
      items={filteredItems}
      itemToString={option => option?.label || ''}
      selectedItem={selectedItem}
      inputValue={inputValue}
      onSelectedItemChange={({selectedItem}) => {
        onChange(selectedItem?.value || '');
      }}
      onInputValueChange={handleInputValueChange}
      renderItem={options => (
        <div className="flex flex-row items-center gap-2">
          <span
            className={cn(
              getPartOfTheDayClasses(options.value as PartOfTheDay)?.icon
            )}
          >
            {getPartOfTheDayIcon(options.value as PartOfTheDay) || <Clock />}
          </span>
          {options.label}
        </div>
      )}
      {...props}
    />
  );
};

export default TaskTimeInput;
